'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Skeleton } from '@common/components';
import { SvgMedicalCalendar } from '@common/icon';
import { useQuery } from '@tanstack/react-query';
import { UnauthQueryOptions } from '@/api';
import { isBot } from '@/utils/isBot';
import { ListItem } from './ListItem';
interface MedicalResourceSectionProps {
  emergencyRoomId: number;
  babyRoomTotalCount?: string;
  roomTotalCount?: string;
}
const EMPTY_STRING = '해당 없음';
export const MedicalResourceSection = t0 => {
  const $ = _c(32);
  const {
    emergencyRoomId,
    babyRoomTotalCount,
    roomTotalCount
  } = t0;
  let t1;
  if ($[0] !== emergencyRoomId) {
    t1 = UnauthQueryOptions["get:/wello/v2/allows/emergency-room/{emergencyRoomId}/live"]({
      path: {
        emergencyRoomId
      }
    });
    $[0] = emergencyRoomId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = isBot();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = {
      ...t1,
      enabled: !t2,
      refetchInterval: 60000,
      select: _temp
    };
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data,
    isLoading
  } = useQuery(t3);
  let t4;
  if ($[5] !== data) {
    t4 = data ?? {};
    $[5] = data;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    babyRoomCount,
    roomCount
  } = t4;
  const t5 = data?.doctorName;
  let emptyMedicalSupportList;
  let t6;
  if ($[7] !== data?.ambulanceUsable || $[8] !== data?.ctUsable || $[9] !== data?.mriUsable || $[10] !== t5) {
    const medicalSupportList = [["\uC804\uBB38\uC758 \uAE30\uBCF8 \uC815\uBCF4", "\uC804\uBB38\uC758", t5], ["CT \uAC00\uC6A9 \uC5EC\uBD80", "CT", data?.ctUsable], ["MRI \uAC00\uC6A9 \uC5EC\uBD80", "MRI", data?.mriUsable], ["\uAD6C\uAE09\uCC28 \uC6B4\uC601 \uC5EC\uBD80", "\uAD6C\uAE09\uCC28", data?.ambulanceUsable]] as const;
    emptyMedicalSupportList = [];
    t6 = medicalSupportList.filter(t7 => {
      const [, shortTitle, value] = t7;
      const isFilled = value !== null;
      if (!isFilled) {
        emptyMedicalSupportList.push(shortTitle);
      }
      return isFilled;
    });
    $[7] = data?.ambulanceUsable;
    $[8] = data?.ctUsable;
    $[9] = data?.mriUsable;
    $[10] = t5;
    $[11] = emptyMedicalSupportList;
    $[12] = t6;
  } else {
    emptyMedicalSupportList = $[11];
    t6 = $[12];
  }
  const filledMedicalSupportList = t6;
  const hasMedicalSupportInfo = !!filledMedicalSupportList.length;
  const emptyMedicalSupportText = emptyMedicalSupportList.length ? `이 병원은 ${emptyMedicalSupportList.join(", ")}와 관련된 정보를 제공하지 않고 있습니다. ` : null;
  const hasRoomCount = roomCount && roomTotalCount;
  const hasBabyRoomCount = babyRoomCount && babyRoomTotalCount;
  const noRoomCount = !hasRoomCount && !hasBabyRoomCount;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <h3 className="mb-12 size-fit gap-8 font-500 text-Gray-900 flex-center typo-body4"><SvgMedicalCalendar className="size-16 text-G-500" />의료자원</h3>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== emptyMedicalSupportText || $[15] !== filledMedicalSupportList || $[16] !== hasMedicalSupportInfo || $[17] !== isLoading) {
    t8 = isLoading ? <div className="flex flex-col gap-8"><Skeleton className="h-21 w-120" /><Skeleton className="h-21 w-170" /><Skeleton className="h-21 w-130" /><Skeleton className="h-21 w-150" /></div> : <ul className="flex flex-col gap-8">{hasMedicalSupportInfo ? <>{filledMedicalSupportList.map(_temp2)}{emptyMedicalSupportText ? <li className="ml-8 flex gap-7 text-Gray-800 typo-body4"><div className={"before:content-['\xB7']"} />{emptyMedicalSupportText}</li> : null}</> : <li className="ml-8 flex gap-7 text-Gray-800 typo-body4"><div className={"before:content-['\xB7']"} />이 병원은 의료 자원과 관련된 정보를 제공하지 않습니다.</li>}</ul>;
    $[14] = emptyMedicalSupportText;
    $[15] = filledMedicalSupportList;
    $[16] = hasMedicalSupportInfo;
    $[17] = isLoading;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h3 className="mb-12 mt-32 size-fit gap-8 font-500 text-Gray-900 flex-center typo-body4"><SvgMedicalCalendar className="size-16 text-G-500" />응급실 가용여부</h3>;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== babyRoomCount || $[21] !== babyRoomTotalCount || $[22] !== hasBabyRoomCount || $[23] !== hasRoomCount || $[24] !== isLoading || $[25] !== noRoomCount || $[26] !== roomCount || $[27] !== roomTotalCount) {
    t10 = isLoading ? <div className="flex flex-col gap-8"><Skeleton className="h-21 w-120" /><Skeleton className="h-21 w-170" /></div> : noRoomCount ? <ul><ListItem>이 병원은 응급실 병상수와 관련된 정보를 제공하지 않습니다.</ListItem></ul> : <ul><ListItem>응급실 일반 가용 병상수 / 기준 병상수 :{" "}{hasRoomCount ? <><span className="text-G-500">{roomCount}</span>/{roomTotalCount}</> : EMPTY_STRING}</ListItem><ListItem>응급실 소아 가용 병상수 / 기준 병상수 :{" "}{hasBabyRoomCount ? <><span className="text-G-500">{babyRoomCount}</span>/<span>{babyRoomTotalCount}</span></> : EMPTY_STRING}</ListItem></ul>;
    $[20] = babyRoomCount;
    $[21] = babyRoomTotalCount;
    $[22] = hasBabyRoomCount;
    $[23] = hasRoomCount;
    $[24] = isLoading;
    $[25] = noRoomCount;
    $[26] = roomCount;
    $[27] = roomTotalCount;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t8) {
    t11 = <section className="flex flex-col">{t7}{t8}{t9}{t10}</section>;
    $[29] = t10;
    $[30] = t8;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
};
function _temp(t0) {
  const {
    context
  } = t0;
  return {
    roomCount: context?.hv36,
    babyRoomCount: context?.hv37,
    ambulanceUsable: context?.hvamyn,
    ctUsable: context?.hvctayn,
    mriUsable: context?.hvmriayn,
    doctorName: context?.hvdnm
  };
}
function _temp2(t0) {
  const [title, shortTitle_0, value_0] = t0;
  const isAvailable = value_0 === "Y";
  return <ListItem key={shortTitle_0}>{title} :<Badge className={isAvailable ? "text-G-600" : "text-R-300"}>{isAvailable ? "\uAC00\uB2A5" : "\uBD88\uAC00\uB2A5"}</Badge></ListItem>;
}